import {
  ColumnFiltersState,
  PaginationState,
  RowData,
  SortingState,
  Table,
  TableOptions,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { TableStateManager } from '@common/components/table/types';
import { useControlledPagination } from './useControlledPagination';
import { useControlledColumnFilters } from './useControlledColumnFilters';
import { useControlledSorting } from './useControlledSorting';

export type ControlledState = {
  pagination: PaginationState;
  sorting: SortingState;
  columnFilters: ColumnFiltersState;
};

export type Tools = {
  resetPagination: () => void;
  enabled: boolean;
};

export function useControlledTable<TData extends RowData>({
  initialPageSize = 10,
  initialSorting = [],
  tableStateManager,
}: {
  initialPageSize: number;
  initialSorting: SortingState;
  tableStateManager: TableStateManager;
}): [
  ControlledState,
  Table<TData> | undefined,
  Partial<TableOptions<TData>>,
  Tools,
  (table: Table<TData>) => void,
] {
  const [table, setTable] = useState<Table<TData> | undefined>(undefined);

  const { paginationState, onPaginationChange, resetPagination } =
    useControlledPagination({ initialPageSize, tableStateManager });

  const { sortingState, onSortingChange } = useControlledSorting({
    resetPagination,
    initialState: initialSorting,
    tableStateManager,
  });

  const { columnFiltersState, onColumnFiltersChange } =
    useControlledColumnFilters({
      resetPagination,
      tableStateManager,
    });

  const state = useMemo(
    () => ({
      pagination: paginationState,
      sorting: sortingState,
      columnFilters: columnFiltersState,
    }),
    [paginationState, sortingState, columnFiltersState],
  );

  const reactTableProps = useMemo(
    () => ({
      state,
      onPaginationChange,
      onSortingChange,
      onColumnFiltersChange,
      manualFiltering: true,
      manualPagination: true,
      manualSorting: true,
    }),
    [state, onPaginationChange, onSortingChange, onColumnFiltersChange],
  );

  return [
    state,
    table,
    reactTableProps,
    {
      resetPagination,
      enabled: !!table,
    },
    setTable,
  ];
}
